import { motion, useReducedMotion, type HTMLMotionProps } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { PRESS, SPRING_CONTROL } from "./motion";

export type Domain = "neutral" | "light" | "climate" | "energy" | "security" | "flow";

const tileVariants = cva(
  "relative flex flex-col gap-2 rounded-2xl bg-tile p-4 text-tile-foreground shadow-tile transition-[box-shadow,background-color] duration-250 ease-signature select-none",
  {
    variants: {
      domain: {
        neutral: "",
        light: "",
        climate: "",
        energy: "",
        security: "",
        flow: "",
      },
      active: { true: "", false: "" },
      interactive: {
        true: "cursor-pointer outline-none hover:shadow-tile-hover focus-visible:ring-[3px] focus-visible:ring-ring/50",
        false: "",
      },
    },
    compoundVariants: [
      // neutral has no active wash on purpose: a neutral tile keeps bg-tile when active.
      { domain: "light", active: true, class: "bg-domain-light-wash text-domain-light-foreground" },
      {
        domain: "climate",
        active: true,
        class: "bg-domain-climate-wash text-domain-climate-foreground",
      },
      {
        domain: "energy",
        active: true,
        class: "bg-domain-energy-wash text-domain-energy-foreground",
      },
      {
        domain: "security",
        active: true,
        class: "bg-domain-security-wash text-domain-security-foreground",
      },
      { domain: "flow", active: true, class: "bg-domain-flow-wash text-domain-flow-foreground" },
    ],
    defaultVariants: { domain: "neutral", active: false, interactive: false },
  },
);

export type TileProps = HTMLMotionProps<"div"> & VariantProps<typeof tileVariants>;

export function Tile({ className, domain, active, interactive, ...props }: TileProps) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      data-slot="tile"
      data-domain={domain ?? "neutral"}
      data-active={active ? "true" : undefined}
      // Keyboard activation (Enter / Space) is the responsibility of the composing
      // component (e.g. DeviceTile); this base provides the ARIA role and focus style only.
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      whileTap={interactive && !reduced ? PRESS : undefined}
      transition={SPRING_CONTROL}
      className={cn(tileVariants({ domain, active, interactive }), className)}
      {...props}
    />
  );
}
